/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetThesesRow, GetThesisRow, GetThesisReplyEligibilityRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { ThesisEventSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── live re-rank · shared comparators ─────────────────────────────────────

function _toBigIntOrZero(v: unknown): bigint {
  if (typeof v === 'bigint') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? BigInt(Math.trunc(v)) : 0n;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '' || !/^-?\d+$/.test(t)) return 0n;
    try {
      return BigInt(t);
    } catch {
      return 0n;
    }
  }
  return 0n;
}

function _cmpD6(a: unknown, b: unknown): number {
  const av = _toBigIntOrZero(a);
  const bv = _toBigIntOrZero(b);
  return av < bv ? -1 : av > bv ? 1 : 0;
}

function _cmpNum(a: unknown, b: unknown): number {
  const av = typeof a === 'number' ? a : Number(a);
  const bv = typeof b === 'number' ? b : Number(b);
  const an = Number.isFinite(av) ? av : 0;
  const bn = Number.isFinite(bv) ? bv : 0;
  return an < bn ? -1 : an > bn ? 1 : 0;
}

function _cmpStr(a: unknown, b: unknown): number {
  const av = a == null ? '' : String(a);
  const bv = b == null ? '' : String(b);
  return av < bv ? -1 : av > bv ? 1 : 0;
}


// ─── useGetTheses ────────────────────────────────────────────────────────────

export interface useGetThesesArgs { readonly marketPubkey?: string; readonly authorId?: string; readonly walletPubkeys?: readonly string[]; readonly category?: string; readonly sort?: string; readonly marketStatus?: string; readonly dedupePerMarket?: string; readonly topPerOutcome?: string; readonly viewportGated?: boolean; }


async function _useGetThesesQueryFn(
  args: useGetThesesArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetThesesRow>> {
  const url = '/v1/theses';
  const params = new URLSearchParams();

  if (args.marketPubkey !== undefined) params.set('marketPubkey', String(args.marketPubkey));
  if (args.authorId !== undefined) params.set('authorId', String(args.authorId));
  if (args.category !== undefined) params.set('category', String(args.category));
  if (args.sort !== undefined) params.set('sort', String(args.sort));
  if (args.marketStatus !== undefined) params.set('marketStatus', String(args.marketStatus));
  if (args.dedupePerMarket !== undefined) params.set('dedupePerMarket', String(args.dedupePerMarket));
  if (args.topPerOutcome !== undefined) params.set('topPerOutcome', String(args.topPerOutcome));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetTheses',
    routeTemplate: '/v1/theses',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetThesesRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
    _sort: { by: args.sort ?? null, order: null },
  };
}


const _useGetThesesFactory = Object.assign(
  (args: useGetThesesArgs) => ({
    queryKey: ['theses', 'list', { marketPubkey: args.marketPubkey ?? null, authorId: args.authorId ?? null, category: args.category ?? null, sort: args.sort ?? null, marketStatus: args.marketStatus ?? null, dedupePerMarket: args.dedupePerMarket ?? null, topPerOutcome: args.topPerOutcome ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetThesesQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetTheses',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['theses', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetThesesArgs, PagedData<GetThesesRow>>;


export const useGetThesesSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/theses',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetThesesFactory },
  parseInput: (raw: unknown): useGetThesesArgs => raw as useGetThesesArgs,
  parseResponse: (raw: unknown): PagedData<GetThesesRow> => raw as PagedData<GetThesesRow>,
  specName: 'useGetTheses' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetThesesRow>>;
export const useGetThesesPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetThesesArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetThesesQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;


// ─── useGetTheses · live re-rank ──────────────────────────────────────────────

/** Sort-key value → { row field, comparator } — from `sort.keys`. */
const _useGetThesesSortKeys: Record<
  string,
  { readonly field: string; readonly cmp: (a: unknown, b: unknown) => number }
> = {
  'recent': { field: 'createdAt', cmp: _cmpStr },
};

/**
 * Pure client-side re-sort of the loaded `GetThesesRow` rows by the active
 * sort key. `by` indexes `_useGetThesesSortKeys`; `order` is `asc`/`desc`
 * (null ⇒ `desc`). Ties break on `signature` ascending. Returns
 * the input array unchanged when `by` is null / unknown.
 */
function resortGetThesesRows(
  rows: readonly GetThesesRow[],
  by: string | null,
  order: string | null,
): readonly GetThesesRow[] {
  if (by === null) return rows;
  const _k = _useGetThesesSortKeys[by];
  if (_k === undefined) return rows;
  const _dir = order === 'asc' ? 1 : -1;
  const _out = rows.slice();
  _out.sort((a, b) => {
    const _c = _k.cmp(
      (a as unknown as Record<string, unknown>)[_k.field],
      (b as unknown as Record<string, unknown>)[_k.field],
    );
    if (_c !== 0) return _c * _dir;
    return _cmpStr(
      (a as unknown as Record<string, unknown>)['signature'],
      (b as unknown as Record<string, unknown>)['signature'],
    );
  });
  return _out;
}

/**
 * Envelope wrapper — re-ranks `env.data` by `env._sort` and preserves
 * `_sort` on the result. The realtime reducer routes every row-patch
 * return through this so a live list never drifts out of sort order.
 */
function _useGetThesesResort(
  env: PagedData<GetThesesRow>,
): PagedData<GetThesesRow> {
  const _by = env._sort?.by ?? null;
  if (_by === null) return env;
  const _resorted = resortGetThesesRows(env.data, _by, env._sort?.order ?? null);
  return _resorted === env.data ? env : { ...env, data: _resorted };
}


function _useGetThesesSubjectArgValues(value: unknown): readonly string[] {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const values = new Set<string>();
  for (const rawValue of rawValues) {
    if (typeof rawValue !== 'string') continue;
    const normalized = rawValue.trim();
    if (normalized !== '') values.add(normalized);
  }
  return [...values];
}

function _useGetThesesHasSubjectArg(value: unknown): boolean {
  return _useGetThesesSubjectArgValues(value).length > 0;
}

function _useGetThesesPerEntitySubjects(args: useGetThesesArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [...(_useGetThesesHasSubjectArg(args.marketPubkey) && !_useGetThesesHasSubjectArg(args.walletPubkeys) ? [`morfi.market.${args.marketPubkey}.thesis.v0`] : []), ...(_useGetThesesHasSubjectArg(args.marketPubkey) && !_useGetThesesHasSubjectArg(args.walletPubkeys) ? [`morfi.market.${args.marketPubkey}.user.*.thesis.v0`] : []), ...(_useGetThesesHasSubjectArg(args.marketPubkey) && _useGetThesesHasSubjectArg(args.walletPubkeys) ? [..._useGetThesesSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.${args.marketPubkey}.user.${_subjectArg0}.thesis.v0`)] : []), ...(!_useGetThesesHasSubjectArg(args.marketPubkey) && _useGetThesesHasSubjectArg(args.walletPubkeys) ? [..._useGetThesesSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.*.user.${_subjectArg0}.thesis.v0`)] : []), ...((args as Record<string, unknown>)['sort'] === 'recent' ? [...(_useGetThesesHasSubjectArg(args.marketPubkey) && !_useGetThesesHasSubjectArg(args.walletPubkeys) ? [`morfi.market.${args.marketPubkey}.thesis.v0`] : [])] : []), ...((args as Record<string, unknown>)['sort'] === 'recent' ? [...(_useGetThesesHasSubjectArg(args.marketPubkey) && _useGetThesesHasSubjectArg(args.walletPubkeys) ? [..._useGetThesesSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.${args.marketPubkey}.user.${_subjectArg0}.thesis.v0`)] : [])] : []), ...((args as Record<string, unknown>)['sort'] === 'recent' ? [...(!_useGetThesesHasSubjectArg(args.marketPubkey) && _useGetThesesHasSubjectArg(args.walletPubkeys) ? [..._useGetThesesSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.*.user.${_subjectArg0}.thesis.v0`)] : [])] : [])];
  return [...new Set(_subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  ))];
}

function _useGetThesesRowSubjectTemplates(args: useGetThesesArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [...(_useGetThesesHasSubjectArg(args.marketPubkey) && !_useGetThesesHasSubjectArg(args.walletPubkeys) ? [`morfi.market.${args.marketPubkey}.thesis.v0`] : []), ...(_useGetThesesHasSubjectArg(args.marketPubkey) && !_useGetThesesHasSubjectArg(args.walletPubkeys) ? [`morfi.market.${args.marketPubkey}.user.*.thesis.v0`] : []), ...(_useGetThesesHasSubjectArg(args.marketPubkey) && _useGetThesesHasSubjectArg(args.walletPubkeys) ? [..._useGetThesesSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.${args.marketPubkey}.user.${_subjectArg0}.thesis.v0`)] : []), ...(!_useGetThesesHasSubjectArg(args.marketPubkey) && _useGetThesesHasSubjectArg(args.walletPubkeys) ? [..._useGetThesesSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.*.user.${_subjectArg0}.thesis.v0`)] : [])];
  return _tmpls.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetThesesReconcileAlwaysSubjects(args: useGetThesesArgs): readonly string[] {
  const _subs: readonly string[] = [...(_useGetThesesHasSubjectArg(args.marketPubkey) && !_useGetThesesHasSubjectArg(args.walletPubkeys) ? [`morfi.market.${args.marketPubkey}.thesis.v0`] : []), ...(_useGetThesesHasSubjectArg(args.marketPubkey) && !_useGetThesesHasSubjectArg(args.walletPubkeys) ? [`morfi.market.${args.marketPubkey}.user.*.thesis.v0`] : []), ...(_useGetThesesHasSubjectArg(args.marketPubkey) && _useGetThesesHasSubjectArg(args.walletPubkeys) ? [..._useGetThesesSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.${args.marketPubkey}.user.${_subjectArg0}.thesis.v0`)] : []), ...(!_useGetThesesHasSubjectArg(args.marketPubkey) && _useGetThesesHasSubjectArg(args.walletPubkeys) ? [..._useGetThesesSubjectArgValues(args.walletPubkeys).map((_subjectArg0) => `morfi.market.*.user.${_subjectArg0}.thesis.v0`)] : [])];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}
function _useGetThesesApplyEvent(
  prev: PagedData<GetThesesRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetThesesArgs,
): PagedData<GetThesesRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // morfi.market.${m}.thesis.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.thesis\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(ThesisEventSchema, _d)
            : fromBinary(ThesisEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof ThesisEventSchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetThesesRow;
        let updated: GetThesesRow = _rowOrig;
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _useGetThesesResort({ ...prev, data: nextRows });
    }
  }

  // morfi.market.${m}.user.*.thesis.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.thesis\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(ThesisEventSchema, _d)
            : fromBinary(ThesisEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof ThesisEventSchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetThesesRow;
        let updated: GetThesesRow = _rowOrig;
        updated = (HELPERS.retainParticipantRelation as unknown as (prev: GetThesesRow, raw: ReturnType<typeof fromBinary<typeof ThesisEventSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetThesesRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _useGetThesesResort({ ...prev, data: nextRows });
    }
  }

  // morfi.market.${m}.user.*.thesis.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.([^.]+)\.thesis\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(ThesisEventSchema, _d)
            : fromBinary(ThesisEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof ThesisEventSchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetThesesRow;
        let updated: GetThesesRow = _rowOrig;
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _useGetThesesResort({ ...prev, data: nextRows });
    }
  }

  // morfi.market.${m}.user.*.thesis.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.[^.]+\.user\.([^.]+)\.thesis\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(ThesisEventSchema, _d)
            : fromBinary(ThesisEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof ThesisEventSchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetThesesRow;
        let updated: GetThesesRow = _rowOrig;
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return _useGetThesesResort({ ...prev, data: nextRows });
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

function _useGetThesesOnEvent(
  args: useGetThesesArgs,
  queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown) => void },
): (ev: MorfiEvent<unknown>) => void {
  const p0 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  const p1 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  const p2 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  return (ev: MorfiEvent<unknown>): void => {
    const subject = ev.subject;
    if (typeof subject !== 'string') return;
    // Each prepend branch decodes its own row var with ITS channel's
    // @morfi/proto schema (different literals may carry different
    // messages) — see emitDecodeRaw. No shared decode here.
      // morfi.market.${m}.thesis.v0
      if (/^morfi\.market\.[^.]+\.thesis\.v0$/.test(subject) && _useGetThesesHasSubjectArg(args.marketPubkey) && !_useGetThesesHasSubjectArg(args.walletPubkeys)) {
        if ((args as Record<string, unknown>)['sort'] !== 'recent') return;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(ThesisEventSchema, _d)
            : fromBinary(ThesisEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof ThesisEventSchema>>;
        if (String((((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['kind']) ?? ((raw as Record<string, unknown> | undefined)?.['kind']))) !== '1') return;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['signature']) ?? ((raw as Record<string, unknown> | undefined)?.['signature']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p0.pending.add(_id);
        if (p0.timer === null) {
          p0.timer = setTimeout(() => {
            p0.timer = null;
            const ids = [...p0.pending];
            p0.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetThesesFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/theses`;
              const params = new URLSearchParams();
              // The backend's `?<filterParam>=id,id,...` mode returns the
              // FULL row shape keyed by id — the parent's `fields` param
              // is deliberately NOT forwarded (incompatible with the keyed
              // response). ALL pending ids are fetched (not only ids absent
              // from the cache) so an EDIT / REACTION / REPLY event
              // re-fetches the changed row, not just brand-new posts.
              if (args.marketPubkey !== undefined) params.set('marketPubkey', Array.isArray(args.marketPubkey) ? args.marketPubkey.join(',') : String(args.marketPubkey));
              if (args.authorId !== undefined) params.set('authorId', Array.isArray(args.authorId) ? args.authorId.join(',') : String(args.authorId));
              if (args.category !== undefined) params.set('category', Array.isArray(args.category) ? args.category.join(',') : String(args.category));
              if (args.sort !== undefined) params.set('sort', Array.isArray(args.sort) ? args.sort.join(',') : String(args.sort));
              if (args.marketStatus !== undefined) params.set('marketStatus', Array.isArray(args.marketStatus) ? args.marketStatus.join(',') : String(args.marketStatus));
              if (args.dedupePerMarket !== undefined) params.set('dedupePerMarket', Array.isArray(args.dedupePerMarket) ? args.dedupePerMarket.join(',') : String(args.dedupePerMarket));
              if (args.topPerOutcome !== undefined) params.set('topPerOutcome', Array.isArray(args.topPerOutcome) ? args.topPerOutcome.join(',') : String(args.topPerOutcome));
              params.set('signatures', ids.join(','));
              params.set('limit', String(ids.length));

              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetTheses.prepend',
                routeTemplate: '/v1/theses',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetTheses prepend fetch failed:', res.status);
                return;
              }
              const json = (await res.json()) as { data?: unknown };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const fetched: ReadonlyArray<GetThesesRow> = Array.isArray(rawData)
                ? (rawData as ReadonlyArray<GetThesesRow>)
                : rawData !== null && typeof rawData === 'object'
                  ? (Object.values(rawData) as ReadonlyArray<GetThesesRow>)
                  : [];
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as { readonly data: ReadonlyArray<GetThesesRow> };
                // Reconcile the re-fetched rows into the cached list:
                //   • id present in cache → REPLACE in place (edit/reaction/reply)
                //   • id requested but absent from the response → REMOVE (deleted)
                //   • id new → PREPEND at the head (newest first)
                const idOf = (r: GetThesesRow): string =>
                  String((r as unknown as Record<string, unknown>)['signature'] ?? '');
                const fresh = new Map<string, GetThesesRow>();
                for (const r of fetched) fresh.set(idOf(r), r);
                const requested = new Set<string>(ids);
                let changed = false;
                const kept: GetThesesRow[] = [];
                for (const row of pd.data) {
                  const k = idOf(row);
                  const updated = fresh.get(k);
                  if (updated !== undefined) {
                    kept.push(updated);
                    fresh.delete(k);
                    changed = true;
                  } else if (requested.has(k)) {
                    // requested but not returned → the row was deleted
                    changed = true;
                  } else {
                    kept.push(row);
                  }
                }
                // `fresh` now holds only ids NOT already cached — brand-new
                // rows; prepend them (head = newest).
                const newRows = fetched.filter((r) => fresh.has(idOf(r)));
                if (newRows.length > 0) changed = true;
                if (!changed) return prev;
                const merged = [...newRows, ...kept];
                const capped =
                  merged.length > 200
                    ? merged.slice(0, 200)
                    : merged;
                return { ...pd, data: capped } as typeof prev;
              });
            })();
          }, 1000);
        }
      }
      // morfi.market.${m}.user.*.thesis.v0
      if (/^morfi\.market\.[^.]+\.user\.[^.]+\.thesis\.v0$/.test(subject) && _useGetThesesHasSubjectArg(args.marketPubkey) && _useGetThesesHasSubjectArg(args.walletPubkeys)) {
        if ((args as Record<string, unknown>)['sort'] !== 'recent') return;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(ThesisEventSchema, _d)
            : fromBinary(ThesisEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof ThesisEventSchema>>;
        if (String((((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['kind']) ?? ((raw as Record<string, unknown> | undefined)?.['kind']))) !== '1') return;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['signature']) ?? ((raw as Record<string, unknown> | undefined)?.['signature']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p1.pending.add(_id);
        if (p1.timer === null) {
          p1.timer = setTimeout(() => {
            p1.timer = null;
            const ids = [...p1.pending];
            p1.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetThesesFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/theses`;
              const params = new URLSearchParams();
              // The backend's `?<filterParam>=id,id,...` mode returns the
              // FULL row shape keyed by id — the parent's `fields` param
              // is deliberately NOT forwarded (incompatible with the keyed
              // response). ALL pending ids are fetched (not only ids absent
              // from the cache) so an EDIT / REACTION / REPLY event
              // re-fetches the changed row, not just brand-new posts.
              if (args.marketPubkey !== undefined) params.set('marketPubkey', Array.isArray(args.marketPubkey) ? args.marketPubkey.join(',') : String(args.marketPubkey));
              if (args.authorId !== undefined) params.set('authorId', Array.isArray(args.authorId) ? args.authorId.join(',') : String(args.authorId));
              if (args.category !== undefined) params.set('category', Array.isArray(args.category) ? args.category.join(',') : String(args.category));
              if (args.sort !== undefined) params.set('sort', Array.isArray(args.sort) ? args.sort.join(',') : String(args.sort));
              if (args.marketStatus !== undefined) params.set('marketStatus', Array.isArray(args.marketStatus) ? args.marketStatus.join(',') : String(args.marketStatus));
              if (args.dedupePerMarket !== undefined) params.set('dedupePerMarket', Array.isArray(args.dedupePerMarket) ? args.dedupePerMarket.join(',') : String(args.dedupePerMarket));
              if (args.topPerOutcome !== undefined) params.set('topPerOutcome', Array.isArray(args.topPerOutcome) ? args.topPerOutcome.join(',') : String(args.topPerOutcome));
              params.set('signatures', ids.join(','));
              params.set('limit', String(ids.length));

              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetTheses.prepend',
                routeTemplate: '/v1/theses',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetTheses prepend fetch failed:', res.status);
                return;
              }
              const json = (await res.json()) as { data?: unknown };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const fetched: ReadonlyArray<GetThesesRow> = Array.isArray(rawData)
                ? (rawData as ReadonlyArray<GetThesesRow>)
                : rawData !== null && typeof rawData === 'object'
                  ? (Object.values(rawData) as ReadonlyArray<GetThesesRow>)
                  : [];
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as { readonly data: ReadonlyArray<GetThesesRow> };
                // Reconcile the re-fetched rows into the cached list:
                //   • id present in cache → REPLACE in place (edit/reaction/reply)
                //   • id requested but absent from the response → REMOVE (deleted)
                //   • id new → PREPEND at the head (newest first)
                const idOf = (r: GetThesesRow): string =>
                  String((r as unknown as Record<string, unknown>)['signature'] ?? '');
                const fresh = new Map<string, GetThesesRow>();
                for (const r of fetched) fresh.set(idOf(r), r);
                const requested = new Set<string>(ids);
                let changed = false;
                const kept: GetThesesRow[] = [];
                for (const row of pd.data) {
                  const k = idOf(row);
                  const updated = fresh.get(k);
                  if (updated !== undefined) {
                    kept.push(updated);
                    fresh.delete(k);
                    changed = true;
                  } else if (requested.has(k)) {
                    // requested but not returned → the row was deleted
                    changed = true;
                  } else {
                    kept.push(row);
                  }
                }
                // `fresh` now holds only ids NOT already cached — brand-new
                // rows; prepend them (head = newest).
                const newRows = fetched.filter((r) => fresh.has(idOf(r)));
                if (newRows.length > 0) changed = true;
                if (!changed) return prev;
                const merged = [...newRows, ...kept];
                const capped =
                  merged.length > 200
                    ? merged.slice(0, 200)
                    : merged;
                return { ...pd, data: capped } as typeof prev;
              });
            })();
          }, 1000);
        }
      }
      // morfi.market.${m}.user.*.thesis.v0
      if (/^morfi\.market\.[^.]+\.user\.[^.]+\.thesis\.v0$/.test(subject) && !_useGetThesesHasSubjectArg(args.marketPubkey) && _useGetThesesHasSubjectArg(args.walletPubkeys)) {
        if ((args as Record<string, unknown>)['sort'] !== 'recent') return;
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(ThesisEventSchema, _d)
            : fromBinary(ThesisEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof ThesisEventSchema>>;
        if (String((((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['kind']) ?? ((raw as Record<string, unknown> | undefined)?.['kind']))) !== '1') return;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['signature']) ?? ((raw as Record<string, unknown> | undefined)?.['signature']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p2.pending.add(_id);
        if (p2.timer === null) {
          p2.timer = setTimeout(() => {
            p2.timer = null;
            const ids = [...p2.pending];
            p2.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetThesesFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/theses`;
              const params = new URLSearchParams();
              // The backend's `?<filterParam>=id,id,...` mode returns the
              // FULL row shape keyed by id — the parent's `fields` param
              // is deliberately NOT forwarded (incompatible with the keyed
              // response). ALL pending ids are fetched (not only ids absent
              // from the cache) so an EDIT / REACTION / REPLY event
              // re-fetches the changed row, not just brand-new posts.
              if (args.marketPubkey !== undefined) params.set('marketPubkey', Array.isArray(args.marketPubkey) ? args.marketPubkey.join(',') : String(args.marketPubkey));
              if (args.authorId !== undefined) params.set('authorId', Array.isArray(args.authorId) ? args.authorId.join(',') : String(args.authorId));
              if (args.category !== undefined) params.set('category', Array.isArray(args.category) ? args.category.join(',') : String(args.category));
              if (args.sort !== undefined) params.set('sort', Array.isArray(args.sort) ? args.sort.join(',') : String(args.sort));
              if (args.marketStatus !== undefined) params.set('marketStatus', Array.isArray(args.marketStatus) ? args.marketStatus.join(',') : String(args.marketStatus));
              if (args.dedupePerMarket !== undefined) params.set('dedupePerMarket', Array.isArray(args.dedupePerMarket) ? args.dedupePerMarket.join(',') : String(args.dedupePerMarket));
              if (args.topPerOutcome !== undefined) params.set('topPerOutcome', Array.isArray(args.topPerOutcome) ? args.topPerOutcome.join(',') : String(args.topPerOutcome));
              params.set('signatures', ids.join(','));
              params.set('limit', String(ids.length));

              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetTheses.prepend',
                routeTemplate: '/v1/theses',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetTheses prepend fetch failed:', res.status);
                return;
              }
              const json = (await res.json()) as { data?: unknown };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const fetched: ReadonlyArray<GetThesesRow> = Array.isArray(rawData)
                ? (rawData as ReadonlyArray<GetThesesRow>)
                : rawData !== null && typeof rawData === 'object'
                  ? (Object.values(rawData) as ReadonlyArray<GetThesesRow>)
                  : [];
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as { readonly data: ReadonlyArray<GetThesesRow> };
                // Reconcile the re-fetched rows into the cached list:
                //   • id present in cache → REPLACE in place (edit/reaction/reply)
                //   • id requested but absent from the response → REMOVE (deleted)
                //   • id new → PREPEND at the head (newest first)
                const idOf = (r: GetThesesRow): string =>
                  String((r as unknown as Record<string, unknown>)['signature'] ?? '');
                const fresh = new Map<string, GetThesesRow>();
                for (const r of fetched) fresh.set(idOf(r), r);
                const requested = new Set<string>(ids);
                let changed = false;
                const kept: GetThesesRow[] = [];
                for (const row of pd.data) {
                  const k = idOf(row);
                  const updated = fresh.get(k);
                  if (updated !== undefined) {
                    kept.push(updated);
                    fresh.delete(k);
                    changed = true;
                  } else if (requested.has(k)) {
                    // requested but not returned → the row was deleted
                    changed = true;
                  } else {
                    kept.push(row);
                  }
                }
                // `fresh` now holds only ids NOT already cached — brand-new
                // rows; prepend them (head = newest).
                const newRows = fetched.filter((r) => fresh.has(idOf(r)));
                if (newRows.length > 0) changed = true;
                if (!changed) return prev;
                const merged = [...newRows, ...kept];
                const capped =
                  merged.length > 200
                    ? merged.slice(0, 200)
                    : merged;
                return { ...pd, data: capped } as typeof prev;
              });
            })();
          }, 1000);
        }
      }
  };
}
export const useGetThesesRealtime = {
  domain: 'theses' as const,
  domainPins: ['morfi.market.*.thesis.v0', 'morfi.market.*.user.*.thesis.v0', 'morfi.market.*.user.*.thesis.v0', 'morfi.market.*.user.*.thesis.v0'] as readonly string[],
  rowKey: 'signature' as const,
  perEntitySubjects: (args: useGetThesesArgs) => _useGetThesesPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetThesesArgs) => _useGetThesesRowSubjectTemplates(args),
  rerank: (prev: PagedData<GetThesesRow>): PagedData<GetThesesRow> => _useGetThesesResort(prev),
  reconcileAlwaysSubjects: (args: useGetThesesArgs) => _useGetThesesReconcileAlwaysSubjects(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetThesesArgs) =>
    (prev: PagedData<GetThesesRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetThesesRow> | undefined =>
      _useGetThesesApplyEvent(prev, ev, _args),
  onEvent: (args: useGetThesesArgs, queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown) => void }) =>
    _useGetThesesOnEvent(args, queryClient),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetThesesRow>, unknown>;


// ─── useGetThesis ────────────────────────────────────────────────────────────

export interface useGetThesisArgs { readonly signature: string; }


async function _useGetThesisQueryFn(
  args: useGetThesisArgs,
  ctx: QueryFunctionContext,
): Promise<GetThesisRow | null> {
  if (args.signature === undefined || args.signature === null || args.signature === '') return null;
  const url = `/v1/theses/${args.signature}`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetThesis',
    routeTemplate: '/v1/theses/{signature}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetThesisRow;
}


const _useGetThesisFactory = Object.assign(
  (args: useGetThesisArgs) => ({
    queryKey: ['theses', 'detail', args.signature] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetThesisQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetThesis',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['theses', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetThesisArgs, GetThesisRow>;


export const useGetThesisSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/theses/{signature}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetThesisFactory },
  parseInput: (raw: unknown): useGetThesisArgs => raw as useGetThesisArgs,
  parseResponse: (raw: unknown): GetThesisRow => raw as GetThesisRow,
  specName: 'useGetThesis' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetThesisRow>;


// ─── useGetThesisReplyEligibility ────────────────────────────────────────────────────────────

export interface useGetThesisReplyEligibilityArgs { readonly marketPubkey?: string; readonly fields?: readonly ('allowed' | 'reason' | 'minTradeUsdcD6' | 'canTrade')[]; }


async function _useGetThesisReplyEligibilityQueryFn(
  args: useGetThesisReplyEligibilityArgs,
  ctx: QueryFunctionContext,
): Promise<GetThesisReplyEligibilityRow | null> {
  const url = '/v1/theses/reply-eligibility';
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  if (args.marketPubkey !== undefined) params.set('marketPubkey', String(args.marketPubkey));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetThesisReplyEligibility',
    routeTemplate: '/v1/theses/reply-eligibility',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetThesisReplyEligibilityRow;
}


const _useGetThesisReplyEligibilityFactory = Object.assign(
  (args: useGetThesisReplyEligibilityArgs) => ({
    queryKey: ['theses', 'detail', { marketPubkey: args.marketPubkey ?? null }, args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetThesisReplyEligibilityQueryFn(args, ctx),
    staleTime: 5000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetThesisReplyEligibility',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['theses', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetThesisReplyEligibilityArgs, GetThesisReplyEligibilityRow>;


export const useGetThesisReplyEligibilitySpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/theses/reply-eligibility',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetThesisReplyEligibilityFactory },
  parseInput: (raw: unknown): useGetThesisReplyEligibilityArgs => raw as useGetThesisReplyEligibilityArgs,
  parseResponse: (raw: unknown): GetThesisReplyEligibilityRow => raw as GetThesisReplyEligibilityRow,
  specName: 'useGetThesisReplyEligibility' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetThesisReplyEligibilityRow>;
